// src/pages/ProjectPage.jsx
import React, { useEffect } from 'react';
import { useParams, Link as RouterLink, Navigate } from 'react-router-dom';
import { Box, HStack, Heading, Text, Link, Wrap, Image } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { getProject } from '../services/projects';
import Markdown from '../components/Markdown';
import Tag from '../components/Tag';

function ExternalButton({ href, children }) {
  return (
    <Link
      href={href}
      isExternal
      fontSize="sm"
      fontWeight="500"
      px={4}
      py={2}
      borderRadius="md"
      border="1px solid"
      borderColor="border"
      color="ink"
      _hover={{ textDecoration: 'none', borderColor: 'accent', color: 'accent' }}
    >
      {children} ↗
    </Link>
  );
}

export default function ProjectPage() {
  const { slug } = useParams();
  const project = getProject(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!project) return <Navigate to="/#projects" replace />;

  const { title, date, summary, status, tags, cover, repo, demo, content } = project;
  // Si la portada ya aparece dentro del texto, no la repetimos arriba
  const showCover = cover && !content.includes(cover);

  return (
    <Box as="article">
      <Helmet>
        <title>{title} · Michelle Pérez</title>
        <meta name="description" content={summary} />
        <link rel="canonical" href={`https://michelle-perez.web.app/projects/${slug}`} />
      </Helmet>

      <Link
        as={RouterLink}
        to="/#projects"
        display="inline-flex"
        alignItems="center"
        gap={1.5}
        fontSize="sm"
        color="inkDim"
        _hover={{ color: 'accent', textDecoration: 'none' }}
      >
        <ArrowLeft size={15} /> All projects
      </Link>

      <Heading as="h1" mt={8} fontSize={{ base: '3xl', md: '4xl' }} fontWeight="500" color="ink" letterSpacing="-0.02em" lineHeight={1.15}>
        {title}
      </Heading>

      <HStack mt={3} spacing={3} fontSize="sm" color="inkDim">
        <Text>{date.slice(0, 4)}</Text>
        {status && <><Text>·</Text><Text>{status}</Text></>}
      </HStack>

      <Text mt={6} fontSize="lg" lineHeight={1.75} color="ink">{summary}</Text>

      <Wrap mt={5} spacing={2}>{tags.map((t) => <Tag key={t}>{t}</Tag>)}</Wrap>

      {(repo || demo) && (
        <HStack mt={6} spacing={3}>
          {demo && <ExternalButton href={demo}>Live site</ExternalButton>}
          {repo && <ExternalButton href={repo}>Source code</ExternalButton>}
        </HStack>
      )}

      {showCover && (
        <Image src={cover} alt="" mt={10} w="100%" borderRadius="lg" border="1px solid" borderColor="border" bg="white" />
      )}

      <Box mt={4}>
        <Markdown>{content}</Markdown>
      </Box>

      <Box mt={16} pt={8} borderTop="1px solid" borderColor="border">
        <Link as={RouterLink} to="/#projects" color="accent" fontWeight="500" display="inline-flex" alignItems="center" gap={1.5}>
          <ArrowLeft size={15} /> Back to all projects
        </Link>
      </Box>
    </Box>
  );
}
